import React from 'react';
import { Icons } from '@model/common';
import { PopupButton } from '@typeform/embed-react';
import styled from '@emotion/styled';
import { ThemeProps } from '@theme/base';
import { Icon } from '@components/common/generic-icon';
import { ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import { getTranslatedHTML } from '@util/common';
import { useI18NextContext } from '@components/hooks';

/* ***************** *
 *       Types       *
 * ***************** */
export interface FeedBackPopupProps {
  id: string;
  title?: string;
  variables?: any;
  buttonProps: {
    label: string;
    color: ButtonColor;
    variant: ButtonVariant;
    size: ButtonSize;
  };
  buttonIcon?: Icons;
  className?: string;
  hidden?: Record<string, string>;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div(() => ({
  textAlign: 'justify',

  ['.popup-button']: {
    border: 0,
    backgroundColor: 'transparent',
    padding: 0,
    minWidth: 220,
    cursor: 'pointer',

    ['&:focus']: {
      outline: 'none'
    },

    ['&:hover']: {
      backgroundColor: 'transparent'
    }
  }
}));

const FeedbackText: any = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any)
}));

const FeedBackPopupButton = styled.div<{
  color: string;
  variant: ButtonVariant;
  size: ButtonSize;
}>(({ theme, color, variant, size }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 50,
  minWidth: 190,
  borderRadius: 6,
  padding: `${theme.custom.spacing.small}px ${theme.custom.spacing.medium}px`,
  cursor: 'pointer',
  textDecoration: 'none',
  border: 'none',
  fontSize: theme.custom.fontSize.medium,
  fontWeight: 500,
  transition: 'all 0.2s ease-in-out',

  ...(variant === ButtonVariant.OUTLINED && {
    color: theme.custom.states.buttons[color].default,
    border: `2px solid ${theme.custom.states.buttons[color].default}`,
    backgroundColor: 'transparent',
    ':hover': {
      backgroundColor: theme.custom.states.buttons[color].light,
      border: `2px solid ${theme.custom.states.buttons[color].default}`
    },
    ':active': {
      border: `2px solid ${theme.custom.states.buttons[color].hover}`
    },
    ':disabled': {
      color: theme.custom.states.buttons[color].disabled,
      border: `2px solid ${theme.custom.states.buttons[color].disabled}`
    }
  }),

  ...(variant === ButtonVariant.CONTAINED && {
    backgroundColor: theme.custom.states.buttons[color].default,
    color: theme.custom.colors.white,
    border: 'none',
    ':hover': {
      backgroundColor: theme.custom.states.buttons[color].hover
    },
    ':active': {
      backgroundColor: theme.custom.states.buttons[color].hover
    },
    ':disabled': {
      backgroundColor: theme.custom.states.buttons[color].disabled,
      color: theme.custom.colors.white
    }
  }),

  ...(size === ButtonSize.FLUID && {
    width: '100%'
  })
}));

const FeedBackPopupButtonIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  color: 'inherit',
  fontSize: theme.custom.fontSize.medium,
  marginLeft: '8px'
}));

/* ************************
 *    FeedBackPopup       *
 * ************************/

export const FeedBackPopup: React.FC<FeedBackPopupProps> = ({
  id,
  title,
  variables,
  buttonProps,
  buttonIcon,
  className,
  hidden
}) => {
  const t = useI18NextContext();
  const { label, color, variant, size } = buttonProps;

  return (
    <Container className={className}>
      {title && <FeedbackText dangerouslySetInnerHTML={getTranslatedHTML(t, title, variables)} />}
      <PopupButton id={id} className="popup-button btn" hidden={hidden}>
        <FeedBackPopupButton color={color.toLowerCase()} variant={variant} size={size}>
          {t(label)}
          {buttonIcon && <FeedBackPopupButtonIcon name={buttonIcon} />}
        </FeedBackPopupButton>
      </PopupButton>
    </Container>
  );
};
